import { AxiosRequestConfig } from "axios";
import { Modify } from "../../utilities/modifyType";
import { CustomAxios } from "./CustomAxios";
import { ServerError, SuccessResult, ValidationError } from "./ServerResponses";

type BaseServerResponse = {
	success: false;
	validation: false;
	error: false;
};
export type CoolServerError = Modify<
	BaseServerResponse & ServerError,
	{
		error: true;
		offline: boolean;
	}
>;

export type CoolValidationError = Modify<
	BaseServerResponse & ValidationError,
	{
		validation: true;
	}
>;

export type CoolSuccessResult<T> = Modify<
	BaseServerResponse & SuccessResult<T>,
	{
		success: true;
	}
>;

type DefaultServerResult<OutputModel> = CoolServerError | CoolSuccessResult<OutputModel>;
type ValidatedServerResult<OutputModel> = CoolServerError | CoolSuccessResult<OutputModel> | CoolValidationError;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DeserializeModel<T> = (serverModel: any) => T;

const runFetch = async <OutputModel>(url: string, config: AxiosRequestConfig, deserialize?: DeserializeModel<OutputModel>) => {
	const axiosClient = CustomAxios.create();
	const axiosResponse = await axiosClient(url, config);
	const result = axiosResponse.data as ValidatedServerResult<OutputModel>;
	if (result.success && deserialize) {
		result.data = deserialize(result.data);
	}
	return result;
};

const uploadProgressPercent = (progress: ProgressEvent) => {
	return Math.round((progress.loaded * 100) / progress.total);
};

export const WebClient = {
	Get: <OutputModel>(url: string, deserialize?: DeserializeModel<OutputModel>, data?: unknown) =>
		runFetch<OutputModel>(url, { method: "get", data, withCredentials: true }, deserialize) as Promise<DefaultServerResult<OutputModel>>,
	Put: {
		Validated: <OutputModel>(
			url: string,
			data: unknown,
			deserialize?: DeserializeModel<OutputModel>
		): Promise<ValidatedServerResult<OutputModel>> =>
			runFetch<OutputModel>(url, { method: "put", data, withCredentials: true }, deserialize) as Promise<ValidatedServerResult<OutputModel>>,
		Unvalidated: <OutputModel>(url: string, data: unknown, deserialize?: DeserializeModel<OutputModel>) =>
			runFetch<OutputModel>(url, { method: "put", data, withCredentials: true }, deserialize) as Promise<DefaultServerResult<OutputModel>>,
		File: {
			External: <OutputModel>(url: string, file: File, onUploadProgress?: (percent: number) => void, deserialize?: DeserializeModel<OutputModel>) => {
				// S3 upload expects binary not formdata
				return runFetch(
					url,
					{
						method: "put",
						data: file,
						withCredentials: false,
						headers: { Pragma: "no-cache" },
						onUploadProgress: (p) => (onUploadProgress ? onUploadProgress(uploadProgressPercent(p)) : undefined),
					},
					deserialize
				) as Promise<DefaultServerResult<OutputModel>>
			},
			Internal: <OutputModel>(url: string, file: File, onUploadProgress?: (percent: number) => void, deserialize?: DeserializeModel<OutputModel>) => {
				// Internal api uses formdata to handle file upload
				const formData = new FormData();
				formData.append("file", file, file.name);
				return runFetch(
					url,
					{
						method: "put",
						data: formData,
						withCredentials: true,
						headers: { Pragma: "no-cache" },
						onUploadProgress: (p) => (onUploadProgress ? onUploadProgress(uploadProgressPercent(p)) : undefined),
					},
					deserialize
				) as Promise<DefaultServerResult<OutputModel>>
			}
		}
	},
	Post: {
		Validated: <OutputModel>(url: string, data: unknown, deserialize?: DeserializeModel<OutputModel>) =>
			runFetch<OutputModel>(url, { method: "post", data, withCredentials: true }, deserialize) as Promise<ValidatedServerResult<OutputModel>>,
		Unvalidated: <OutputModel>(url: string, data: unknown, deserialize?: DeserializeModel<OutputModel>) =>
			runFetch<OutputModel>(url, { method: "post", data, withCredentials: true }, deserialize) as Promise<DefaultServerResult<OutputModel>>,
		File: {
			External: <OutputModel>(url: string, file: File, onUploadProgress?: (percent: number) => void, deserialize?: DeserializeModel<OutputModel>) => {
				// S3 upload expects binary not formdata
				return runFetch(
					url,
					{
						method: "post",
						data: file,
						withCredentials: true,
						headers: { Pragma: "no-cache" },
						onUploadProgress: (p) => (onUploadProgress ? onUploadProgress(uploadProgressPercent(p)) : undefined),
					},
					deserialize
					) as Promise<DefaultServerResult<OutputModel>>
			},
			Internal: <OutputModel>(url: string, file: File, onUploadProgress?: (percent: number) => void, deserialize?: DeserializeModel<OutputModel>) => {
				// Internal api uses formdata to handle file upload
				const formData = new FormData();
				formData.append("file", file, file.name);
				return runFetch(
					url,
					{
						method: "post",
						data: formData,
						withCredentials: true,
						headers: { Pragma: "no-cache" },
						onUploadProgress: (p) => (onUploadProgress ? onUploadProgress(uploadProgressPercent(p)) : undefined),
					},
					deserialize
					) as Promise<DefaultServerResult<OutputModel>>
			}
		}
	},
	Delete: (url: string) => runFetch(url, { method: "delete", withCredentials: true }, undefined) as Promise<ValidatedServerResult<unknown>>,
};
